import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import FileDropzone, { type UploadedFile } from "./FileDropzone";
import type { VersichererKey } from "./types";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  panel: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "18px",
    paddingRight: "18px",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "12px",
    paddingRight: "12px",
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    backgroundColor: BRAND.pillarGreen,
    color: BRAND.white,
    alignSelf: "flex-start",
    ...shorthands.borderRadius("999px"),
  },
  pillNeutral: {
    backgroundColor: "#f5eef6",
    color: BRAND.ink,
  },
  pillWarn: {
    backgroundColor: "#fff5e6",
    color: "#7a4a10",
  },
  title: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    color: BRAND.ink,
    margin: 0,
  },
  text: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    lineHeight: 1.5,
  },
  label: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
});

export type VereinbarungStatus = "keine" | "moeglich" | "vereinbart";

const VereinbarungSection = ({
  versicherer,
  status,
  file,
  onFileChange,
}: {
  versicherer: VersichererKey;
  status: VereinbarungStatus;
  file: UploadedFile | null;
  onFileChange: (next: UploadedFile | null) => void;
}) => {
  const styles = useStyles();
  const pillClass =
    status === "vereinbart"
      ? styles.pill
      : `${styles.pill} ${status === "moeglich" ? styles.pillWarn : styles.pillNeutral}`;
  const pillText =
    status === "vereinbart" ? "Vereinbart" : status === "moeglich" ? "Möglich" : "Keine Vereinbarung";

  return (
    <div className={styles.root}>
      <div className={styles.panel}>
        <span className={pillClass}>{pillText}</span>
        <h4 className={styles.title}>Status der Vereinbarung</h4>
        <p className={styles.text}>
          {versicherer === "generali" ? (
            <>
              Mit der <strong>Generali</strong> liegt eine Rahmenvereinbarung für die Immobilien-Sparte vor. Die
              vereinbarten Konditionen werden automatisch auf den Vertrag angewendet.
            </>
          ) : (
            <>Bitte wählen Sie zuerst einen Versicherer im vorherigen Schritt.</>
          )}
        </p>
      </div>

      <div className={styles.panel}>
        <span className={styles.label}>Vereinbarungs-Dokument</span>
        <FileDropzone
          label="Vereinbarungs-Dokument auswählen"
          hint="PDF, Word oder Bild — wird lokal mit dem Vertragsentwurf gespeichert."
          file={file}
          onChange={onFileChange}
        />
      </div>
    </div>
  );
};

export default VereinbarungSection;
